import React from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';

import { getSelectedWorkingDay, getTodaysDayName, getColorByDayName } from '../utils';
import { WORKING_DAYS } from '../../constants';
import { type StudentDayStats } from '../../model';

const Container = styled(Stack)`
  justify-content: center;
  border: 1px solid #3169f6;
  border-radius: 12px;
  padding: 16px;
  gap: 24px;
  background-color: #fff;
`;

const Title = styled(Typography)`
  color: #0f1629;
  font-size: 22px;
  font-weight: 400;
`;

const DayItem = styled(Stack)<{ selected?: boolean }>`
  position: relative;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 12px;
  background-color: ${({ selected }) => (selected ? '#fdf5fa' : 'transparent')};
`;

const Triangle = styled(ArrowRightRoundedIcon)`
  position: absolute;
  left: -46px;
  color: #ea0bbd;
  font-size: 60px;
`;

const Dot = styled(Box)<{ day: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ day }) => getColorByDayName(day)};
`;

const DayLabel = styled(Typography)<{ selected?: boolean }>`
  color: #0f1629;
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? 'bold' : '500')};
  transform: translate(0, 1px);
`;

type Props = {
  data: Record<string, StudentDayStats> | undefined;
};

export const StudentProgressHeader = ({ data }: Props) => {
  const todaysDayName = getTodaysDayName();

  return (
    <Container>
      <Title>
        <b>My Progress</b> - Lessons Completed
      </Title>
      <Stack direction="row" justifyContent="space-between">
        {WORKING_DAYS.map((day) => {
          const isSelected = day === getSelectedWorkingDay(todaysDayName, day);
          const isMondaySelected = todaysDayName === 'Monday';
          const daysStats = data?.[day];

          const actual = daysStats?.actual ?? 0;
          const target = daysStats?.target ?? 2;

          return (
            <DayItem key={day} selected={isSelected} ml={isMondaySelected ? 2 : 0}>
              {isSelected && <Triangle />}
              <Dot day={day} />
              <DayLabel selected={isSelected}>{day}</DayLabel>
              <DayLabel selected={isSelected}>
                {actual} of {target}
              </DayLabel>
            </DayItem>
          );
        })}
      </Stack>
    </Container>
  );
};
